import { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { getMessageContext, getFeedById } from '../../../../modules/Feeds/services/Feed.service';
import { generateFeedSettings, transformApiFeedToDisplay } from '../../../../modules/Feeds/mappers/feedUtility';
import { useFeedMessagesMenuHook } from '../../../../shared/hooks';
import { exportConversationMessages } from '../../../../../util/APIUtils';
import { downloadBlobAsFile } from '../../../../modules/Feeds/services/Audit.service';

// Constants
const DEFAULT_MESSAGE_COUNT = 20;
const LOAD_MORE_COUNT = 10;

/**
 * Custom hook for fetching and transforming conversation messages for audit view
 * 
 * @param {Object} conversationData - Conversation data object containing messageId (required)
 * @returns {Object} Hook return object containing:
 *   @returns {Array} messages - Array of transformed messages, or empty array if not loaded
 *   @returns {Object|null} selectedFeed - The conversation feed object, or null if not loaded
 *   @returns {Array} feedSettings - Array of feed settings (tags like Private, View Only, etc.)
 *   @returns {boolean} isLoading - Loading state indicator
 *   @returns {boolean} isLoadingMore - Loading state for loading more messages
 *   @returns {boolean} hasMoreMessages - Whether there are more messages to load
 *   @returns {boolean} showNewMessagesIndicator - Whether to show the new messages indicator
 *   @returns {Error|null} error - Any error that occurred during fetching, or null if no error
 *   @returns {Function} loadMoreMessages - Function to load more messages (older messages)
 *   @returns {Function} loadNewerMessages - Function to load newer messages (newer messages)
 *   @returns {Object} readReceiptsModal - Modal props for read receipts modal
 */
export const useFeedAuditConversationsHook = (conversationData) => {
  const [messages, setMessages] = useState([]);
  const [selectedFeed, setSelectedFeed] = useState(null);
  const [feedSettings, setFeedSettings] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [showNewMessagesIndicator, setShowNewMessagesIndicator] = useState(true);
  const [error, setError] = useState(null);
  const [isExporting, setIsExporting] = useState(false);

  // Get user details from Redux store for message transformation
  const { userDetail } = useSelector((state) => state.app || {});
  const currentUserKey = userDetail?.userId;

  // Hook for message menu options and modal handlers
  const { menuProps, readReceiptsModal } = useFeedMessagesMenuHook();

  // Refs to store latest values to avoid dependency issues
  const conversationDataRef = useRef(conversationData);
  const menuPropsRef = useRef(menuProps);
  const currentUserKeyRef = useRef(currentUserKey);
  const messagesRef = useRef(messages);
  const hasMoreMessagesRef = useRef(hasMoreMessages);
  const isLoadingMoreRef = useRef(isLoadingMore);

  // Update refs when values change
  useEffect(() => {
    conversationDataRef.current = conversationData;
    menuPropsRef.current = menuProps;
    currentUserKeyRef.current = currentUserKey;
    messagesRef.current = messages;
    hasMoreMessagesRef.current = hasMoreMessages;
    isLoadingMoreRef.current = isLoadingMore;
  }, [conversationData, menuProps, currentUserKey, messages, hasMoreMessages, isLoadingMore]);
  /**
   * Helper function to process and merge messages with duplicate detection
   * @param {Array} newMessages - New messages from API response
   * @param {Array} prevMessages - Previous messages in state
   * @param {boolean} prepend - If true, prepend new messages; if false, append
   * @returns {Object} { mergedMessages, hasDuplicates, hasNoNewMessages }
   */
  const processAndMergeMessages = useCallback((newMessages, prevMessages, prepend = false) => {
    const existingIds = new Set(prevMessages.map(msg => msg.id));
    const filteredMessages = newMessages.filter(msg => !existingIds.has(msg.id));
    const hasDuplicates = newMessages.some(msg => existingIds.has(msg.id));
    const hasNoNewMessages = filteredMessages.length === 0;

    const mergedMessages = prepend
      ? [...filteredMessages, ...prevMessages]
      : [...prevMessages, ...filteredMessages];

    return { mergedMessages, hasDuplicates, hasNoNewMessages };
  }, []);

  /**
   * Helper function to filter out the current message from the response
   * @param {Array} messages - Array of messages from API response
   * @param {string} currentMessageId - The ID of the current message to exclude
   * @returns {Array} Filtered array of messages excluding the current message
   */
  const filterOutCurrentMessage = useCallback((messages, currentMessageId) => {
    if (!Array.isArray(messages) || !currentMessageId) {
      return messages || [];
    }
    return messages.filter(msg => msg.id !== currentMessageId);
  }, []);

  const resetState = useCallback(() => {
    setMessages([]);
    setSelectedFeed(null);
    setFeedSettings([]);
    setError(null);
    setIsLoading(false);
    setIsLoadingMore(false);
    setHasMoreMessages(true);
    setShowNewMessagesIndicator(true);
  }, []);

  const fetchMessageContext = useCallback(async () => {
    // Use refs to get latest values without adding them to dependencies
    const conversationData = conversationDataRef.current;
    const menuProps = menuPropsRef.current;
    const currentUserKey = currentUserKeyRef.current;

    if (!conversationData || !conversationData.messageId) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await getMessageContext(conversationData.messageId, {
        currentUserKey,
        menuProps,
        before: 0,
        after: DEFAULT_MESSAGE_COUNT,
      });

      if (!response?.data?.length) {
        setError(new Error('No data received from API'));
        return;
      }

      // Filter out the current message (the one we're fetching context for) from the response
      const filteredMessages = filterOutCurrentMessage(response.data, conversationData.messageId);

      // If the response size (after excluding current message) is less than DEFAULT_MESSAGE_COUNT,
      // it means there are no more newer messages, so set showNewMessagesIndicator to false
      if (filteredMessages.length < DEFAULT_MESSAGE_COUNT) {
        setShowNewMessagesIndicator(false);
      }

      setMessages(response.data);
      setHasMoreMessages(true);

      const feedId = response.feedId;

      // Fetch feed details to get complete feed data and settings
      if (feedId) {
        try {
          const feedDetailsResponse = await getFeedById(feedId);
          if (feedDetailsResponse?.data) {
            // Use transformApiFeedToDisplay to map the feed object with membersInfo
            const transformedFeed = transformApiFeedToDisplay(feedDetailsResponse.data, currentUserKey, 'dm');
            // Generate settings from feed details
            const generatedSettings = generateFeedSettings(feedDetailsResponse.data);
            setFeedSettings(generatedSettings);
            
            // Merge transformed feed with settings
            setSelectedFeed({
              ...transformedFeed,
              settings: generatedSettings,
            });
          } 
        } catch (feedError) {
          // Fallback: use buildFeedObject if feed details fetch fails
          console.error('[useFeedAuditConversationsHook] Failed to fetch feed details:', feedError);
        }
      } 
    } catch (err) {
      console.error('[useFeedAuditConversationsHook] Failed to fetch message context:', err);
      setError(err);
      setMessages([]);
      setSelectedFeed(null);
    } finally {
      setIsLoading(false);
    }
  }, [filterOutCurrentMessage]); // buildFeedObject and filterOutCurrentMessage are stable (no dependencies)

  // Extract messageId to use as a primitive dependency
  const messageId = conversationData?.messageId;

  useEffect(() => {
    // Reset state when messageId or currentUserKey changes
    resetState();

    // If no messageId is provided, return early
    if (!messageId) {
      return;
    }

    fetchMessageContext();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messageId, currentUserKey]); // Only depend on primitive values to avoid re-render loops

  /**
   * Load more messages (older messages) by calling API with the first message's ID
   */
  const loadMoreMessages = useCallback(async () => {
    // Use refs to get latest values without causing callback recreation
    const currentMessages = messagesRef.current;
    const currentIsLoadingMore = isLoadingMoreRef.current;
    const currentHasMoreMessages = hasMoreMessagesRef.current;

    if (currentIsLoadingMore || !currentMessages.length || !currentHasMoreMessages) {
      return;
    }

    const firstMessageId = currentMessages[0]?.id;
    if (!firstMessageId) {
      return;
    }

    setIsLoadingMore(true);
    setError(null);

    try {
      const response = await getMessageContext(firstMessageId, {
        currentUserKey: currentUserKeyRef.current,
        menuProps: menuPropsRef.current,
        before: LOAD_MORE_COUNT,
        after: 0,
      });

      // If response is empty or undefined, set hasMoreMessages to false
      if (!response?.data?.length) {
        setHasMoreMessages(false);
        return;
      }

      const filteredMessages = filterOutCurrentMessage(response.data, firstMessageId);
      if (filteredMessages.length < LOAD_MORE_COUNT) {
        setHasMoreMessages(false);
      }

      setMessages((prevMessages) => {
        const { mergedMessages, hasNoNewMessages } = processAndMergeMessages(
          response.data,
          prevMessages,
          true // prepend older messages
        );

        // Set hasMoreMessages to false if there are no new messages after deduplication
        if (hasNoNewMessages) {
          setHasMoreMessages(false);
        }

        return mergedMessages;
      });
    } catch (err) {
      console.error('[useFeedAuditConversationsHook] Failed to load more messages:', err);
      setError(err);
    } finally {
      setIsLoadingMore(false);
    }
  }, [processAndMergeMessages, filterOutCurrentMessage]);

  /**
   * Load newer messages by calling API with the last message's ID
   */
  const loadNewerMessages = useCallback(async () => {
    // Use refs to get latest values without causing callback recreation
    const currentMessages = messagesRef.current;
    const currentIsLoadingMore = isLoadingMoreRef.current;

    if (currentIsLoadingMore || !currentMessages.length) {
      return;
    }

    const lastMessageId = currentMessages[currentMessages.length - 1]?.id;
    if (!lastMessageId) {
      return;
    }

    setError(null);

    try {
      const response = await getMessageContext(lastMessageId, {
        currentUserKey: currentUserKeyRef.current,
        menuProps: menuPropsRef.current,
        after: LOAD_MORE_COUNT,
        before: 0,
      });

      // If response is empty or undefined, set showNewMessagesIndicator to false
      if (!response?.data?.length) {
        setShowNewMessagesIndicator(false);
        return;
      }

      const filteredMessages = filterOutCurrentMessage(response.data, lastMessageId);
      if (filteredMessages.length < LOAD_MORE_COUNT) {
        setShowNewMessagesIndicator(false);
      }

      setMessages((prevMessages) => {
        const { mergedMessages, hasNoNewMessages } = processAndMergeMessages(
          response.data,
          prevMessages,
          false // append newer messages
        );

        // Set showNewMessagesIndicator to false if there are no new messages after deduplication
        if (hasNoNewMessages) {
          setShowNewMessagesIndicator(false);
        }

        return mergedMessages;
      });
    } catch (err) {
      console.error('[useFeedAuditConversationsHook] Failed to load newer messages:', err);
      setError(err);
    } 
  }, [processAndMergeMessages, filterOutCurrentMessage]);

  /**
   * Export handler for conversation messages
   */
  const handleExportConversation = useCallback(async () => {
    if (!selectedFeed?.id || isExporting) {
      return;
    }

    setIsExporting(true);
    try {
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const blob = await exportConversationMessages(selectedFeed.id, userTimezone);
      
      // Generate filename: Date_Conversation_FeedName.csv
      const dateStr = new Date().toISOString().split('T')[0];
      // Sanitize feed name: remove special characters and replace spaces with nothing
      const feedName = selectedFeed.title 
        ? selectedFeed.title.replace(/[^a-zA-Z0-9 ]/g, '').replace(/\s+/g, '')
        : 'Unknown';
      const filename = `${dateStr}_Conversation_${feedName}.csv`;

      // Download the file using the utility function
      downloadBlobAsFile(blob, filename);
    } catch (error) {
      console.error('Error exporting conversation:', error);
      // Could show a toast/notification here
    } finally {
      setIsExporting(false);
    }
  }, [selectedFeed?.id, selectedFeed?.title, isExporting]);

  return { 
    messages, 
    selectedFeed, 
    feedSettings,
    isLoading, 
    isLoadingMore,
    hasMoreMessages,
    showNewMessagesIndicator,
    error,
    loadMoreMessages,
    loadNewerMessages,
    readReceiptsModal,
    isExporting,
    handleExportConversation
  };
};

